import { z } from "zod";
import { BEModulesClassNames } from "../utils/moduleList";

// ─── Common Zod utilities ───────────────────────────────────────────────────

/** Number or null - used across module response schemas */
export const nullableNumber = z.number().nullable();

/** Note object or null - used in ModuleShared */
const nullableNote = z
	.object({
		id: z.number(),
		content: z.string(),
		module_type: z.number(),
		module_id: z.number(),
	})
	.nullable()
	.optional();

// ─── Module type schema ─────────────────────────────────────────────────────

/**
 * Zod schema for ModuleType - mirrors the ModuleType interface.
 * Used when API returns the full module_type object (not just id).
 * Real API may return null for name_fr/name_es and omit name_en.
 */
export const moduleTypeObjectSchema = z
	.object({
		id: z.number(),
		name: z.string(),
		name_en: z.string().nullable().optional(),
		name_fr: z.string().nullable().optional(),
		name_es: z.string().nullable().optional(),
		is_luc: z.boolean(),
		is_submodule: z.boolean(),
		is_container: z.boolean(),
		class_name: z.nativeEnum(BEModulesClassNames),
	})
	.passthrough();

// ─── Entry schemas (Storage, Input, Energy, etc.) ────────────────────────────

/**
 * Zod schema for EntryShared - shared by all entry types (Storage, Input, Energy, etc.)
 * Mirrors the EntryShared interface: id, module_type, parent
 */
export const entrySharedSchema = z
	.object({
		id: z.number(),
		module_type: z.number().or(moduleTypeObjectSchema).optional(),
		parent: z.number().nullable().optional(),
	})
	.passthrough();

// ─── Main module schemas (Grassland, LandUseChange, etc.) ───────────────────

/**
 * Zod schema for ModuleShared - shared by main activity modules (Grassland, LandUseChange, etc.)
 * Mirrors the ModuleShared interface: activity, start_year, soc_t2_*, data_source, note, status
 * Real API may return status as object (e.g. { id, name }) instead of number.
 */
export const moduleSharedSchema = z
	.object({
		activity: nullableNumber,
		start_year: nullableNumber,
		soc_t2_start: nullableNumber.optional(),
		soc_t2_w: nullableNumber.optional(),
		soc_t2_wo: nullableNumber.optional(),
		data_source: nullableNumber.optional(),
		note: nullableNote,
		status: z.any().optional(),
	})
	.passthrough();

/**
 * Zod schema for LandModule - shared by land-based modules (Grassland, LandUseChange, etc.)
 * Mirrors the LandModule interface: land_use_change, organic_soil, area, land_use_type_*, flu_t2_*, fi_t2_*, fmg_t2_*
 */
export const landModuleSchema = z
	.object({
		land_use_change: nullableNumber.optional(),
		organic_soil: nullableNumber.optional(),
		area: nullableNumber.optional(),
		land_use_type_start: nullableNumber.optional(),
		land_use_type_w: nullableNumber.optional(),
		land_use_type_wo: nullableNumber.optional(),
		land_use_type_thread: nullableNumber.optional(),
		flu_t2_start: nullableNumber.optional(),
		flu_t2_w: nullableNumber.optional(),
		flu_t2_wo: nullableNumber.optional(),
		fi_t2_start: nullableNumber.optional(),
		fi_t2_w: nullableNumber.optional(),
		fi_t2_wo: nullableNumber.optional(),
		fmg_t2_start: nullableNumber.optional(),
		fmg_t2_w: nullableNumber.optional(),
		fmg_t2_wo: nullableNumber.optional(),
	})
	.passthrough();

/**
 * Base schema for main activity modules (id, module_type, created_at, updated_at).
 * Use with moduleSharedSchema and landModuleSchema for grassland, land use change, etc.
 * Real API may omit created_at or return null for updated_at.
 */
export const mainModuleBaseSchema = z
	.object({
		id: z.number(),
		module_type: z.number().or(moduleTypeObjectSchema).optional(),
		created_at: z.string().optional(),
		updated_at: z.string().nullable().optional(),
	})
	.passthrough();
